import { CheckCircle2, Image as ImageIcon, Upload } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { Card, SectionHeading } from '@/components/ui';
import { cn } from '@/lib/cn';
import { readFileAsDataUrl } from '@/lib/image';

interface ImageUploaderProps {
  image: string | null;
  onImageSelect: (dataUrl: string) => void;
}

export function ImageUploader({ image, onImageSelect }: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const accept = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (PNG, JPG or WEBP).');
      return;
    }
    setError(null);
    onImageSelect(await readFileAsDataUrl(file));
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    void accept(event.dataTransfer.files?.[0]);
  };

  return (
    <Card>
      <div className="flex items-center justify-between">
        <SectionHeading
          title="Input Source: Raw Photo"
          description="Upload an under-construction, un-plastered, or raw property photo"
        />
        {image && (
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> RAW LOADED
          </span>
        )}
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label="Upload property photo"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={cn(
          'relative border-2 border-dashed rounded-xl p-4 transition-all cursor-pointer flex flex-col items-center justify-center min-h-[190px] bg-slate-50',
          image
            ? 'border-blue-500/50 hover:border-blue-600'
            : 'border-slate-300 hover:border-blue-500/70 hover:bg-slate-100/50',
        )}
        id="image-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(e) => {
            void accept(e.target.files?.[0]);
            e.target.value = '';
          }}
          className="hidden"
          id="house-photo-input"
        />

        {image ? (
          <div className="relative w-full h-44 flex items-center justify-center overflow-hidden rounded-lg group">
            <img
              src={image}
              alt="Selected house photo"
              className="max-h-full max-w-full object-contain rounded-lg shadow-xs"
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-2xs">
              <span className="text-xs font-semibold text-white bg-slate-900 px-3.5 py-1.5 rounded-lg border border-slate-700 flex items-center gap-1.5 shadow-md">
                <Upload className="w-3.5 h-3.5 text-blue-400" /> Change Source Photo
              </span>
            </div>
            <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur px-2 py-0.5 rounded text-[10px] font-bold text-slate-700 shadow-xs">
              RAW PHOTO
            </div>
          </div>
        ) : (
          <div className="text-center p-4">
            <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 mx-auto mb-2.5 shadow-xs">
              <ImageIcon className="w-5 h-5" />
            </div>
            <p className="text-xs font-bold text-slate-800">Drag & drop property photo here</p>
            <p className="text-[11px] text-slate-400 mt-0.5">Supports PNG, JPG, WEBP (Max 20MB)</p>
            <span className="mt-3 inline-block px-3.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs transition-colors shadow-xs">
              Browse Files
            </span>
          </div>
        )}
      </div>

      {error && <p className="text-xs font-semibold text-rose-600">{error}</p>}
    </Card>
  );
}
